import React, { useEffect, useState } from 'react';
import {
  Box,
  Grid,
  Pagination,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableFooter,
  TableHead,
  TableRow,
  TableSortLabel,
} from '@mui/material';
import { isEmpty, isNil, map, toLower } from 'lodash';
import { useDispatch, useSelector } from 'react-redux';
import { visuallyHidden } from '@mui/utils';

import LoadingModule from 'common/components/LoadingModule';
import NothingFound from 'common/components/NothingFound';
import { SigFieldKeys } from 'enums/SigManagement.enum';
import { loadSigAssociations, updateSigAssociationsSortBy } from 'store/thunks/sigThunks';
import { SortOrder } from 'enums/common.enum';

import SigDetailDialog from './SigDetailDialog';
import SigRow, { SIG_ROW_COLUMN_COUNT } from './SigRow';

// The two counts and Status stay unsortable: the server has no sort key for a count, and Status on
// a sig row is a boolean that would only ever split the page in two.
//
// One thing to know about any of these sorts: the query orders sigs with no directions last first,
// before whichever column was chosen. So a date sort still parks direction-less sigs at the bottom.
const headCells = [
  { id: SigFieldKeys.SIG, label: 'Sig', align: 'left', sortable: true },
  { id: SigFieldKeys.DIRECTION_COUNT, label: 'Directions', align: 'center', sortable: false },
  // "Catalog Items", not "Drugs". An approval is held against one catalog variant — a drug at a
  // strength and pack size — and the dialog that manages them calls them catalog items, so two
  // names for one thing on two screens was read as two different things.
  {
    id: SigFieldKeys.APPROVED_DRUG_COUNT,
    label: 'Catalog Items',
    align: 'center',
    sortable: false,
  },
  // Sortable again. They were switched off because the dates were an aggregate over the sig's
  // directions while the server sorted per-direction, so the ordering did not match the column.
  // Both are sig-level expressions now — Created At is the sig's own timestamp, Updated At the
  // latest of the sig and its directions — and the ORDER BY uses the same expression the column
  // displays.
  { id: SigFieldKeys.CREATED_AT, label: 'Created At', align: 'left', sortable: true },
  { id: SigFieldKeys.UPDATED_AT, label: 'Updated At', align: 'left', sortable: true },
  { id: SigFieldKeys.STATUS, label: 'Status', align: 'left', sortable: false },
];

const SigsTable = () => {
  const dispatch = useDispatch();
  const { sortBy, pages, currentPage, count } = useSelector(({ sigs }) => sigs);
  // The whole row, not just its id: the dialog shows the sig text and status in its header, and the
  // list is replaced on a page change, so holding an id would leave the dialog with nothing to read.
  const [detailSigRow, setDetailSigRow] = useState(null);

  const sigList = pages[currentPage];
  const totalPages = Math.ceil(count / 25);

  const handleSort = (event) => {
    const sortKey = event.currentTarget?.dataset?.headProperty;

    dispatch(
      updateSigAssociationsSortBy({
        sortBy: {
          sortKey,
          sortOrder:
            sortBy.sortKey === sortKey && sortBy.sortOrder === SortOrder.ASCENDING
              ? SortOrder.DESCENDING
              : SortOrder.ASCENDING,
        },
      })
    );
  };

  const handlePageChange = (_, page) => {
    if (currentPage === page) {
      return;
    }
    setDetailSigRow(null);
    dispatch(loadSigAssociations({ page }));
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleOpenSigDetail = (sigRow) => () => setDetailSigRow(sigRow);

  useEffect(() => {
    dispatch(loadSigAssociations({ page: 1 }));
  }, [dispatch]);

  return (
    <Grid component={Paper} container direction='column' sx={{ py: 1, px: 2 }}>
      <Grid item container>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                {headCells.map(({ id, label, align, sortable }) => (
                  <TableCell key={id} align={align}>
                    {!sortable ? (
                      label
                    ) : (
                      <TableSortLabel
                        active={sortBy?.sortKey === id}
                        direction={sortBy?.sortKey === id ? toLower(sortBy?.sortOrder) : 'asc'}
                        onClick={handleSort}
                        data-head-property={id}
                      >
                        {label}
                        {sortBy?.sortKey === id ? (
                          <Box component='span' sx={visuallyHidden}>
                            {toLower(sortBy.sortOrder) === 'desc'
                              ? 'sorted descending'
                              : 'sorted ascending'}
                          </Box>
                        ) : null}
                      </TableSortLabel>
                    )}
                  </TableCell>
                ))}
                <TableCell />
              </TableRow>
            </TableHead>

            <TableBody>
              {map(sigList, (sigRow) => (
                <SigRow
                  key={sigRow.sigId}
                  sigRow={sigRow}
                  onOpenDetail={handleOpenSigDetail(sigRow)}
                />
              ))}
            </TableBody>

            {!isNil(sigList) && totalPages > 1 && (
              <TableFooter>
                <TableRow>
                  <TableCell
                    colSpan={SIG_ROW_COLUMN_COUNT}
                    align='right'
                    sx={{ borderBottom: 'none' }}
                  >
                    <Pagination
                      sx={{ justifyContent: 'flex-end', alignItems: 'flex-end', display: 'flex' }}
                      count={totalPages}
                      page={currentPage}
                      size='small'
                      onChange={handlePageChange}
                    />
                  </TableCell>
                </TableRow>
              </TableFooter>
            )}
          </Table>
        </TableContainer>
      </Grid>

      {isNil(sigList) && (
        <Grid item>
          <LoadingModule />
        </Grid>
      )}

      {!isNil(sigList) && isEmpty(sigList) && (
        <Grid item>
          <NothingFound />
        </Grid>
      )}

      {detailSigRow && (
        <SigDetailDialog sigRow={detailSigRow} onClose={() => setDetailSigRow(null)} />
      )}
    </Grid>
  );
};

export default SigsTable;
